import { useState, type FormEvent } from "react";

type AndroidTesterStatus = "idle" | "sending" | "sent" | "error";

export function AppStoreLinks({ className = "" }: { className?: string }) {
  const [androidModalOpen, setAndroidModalOpen] = useState(false);
  const [androidStatus, setAndroidStatus] = useState<AndroidTesterStatus>("idle");
  const [androidForm, setAndroidForm] = useState({
    name: "",
    google_email: "",
    whatsapp: "",
  });

  function handleAndroidSubmit(e: FormEvent) {
    e.preventDefault();
    if (!androidForm.name.trim() || !androidForm.google_email.trim()) return;

    setAndroidStatus("sending");

    fetch("/android_testers", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content") || "",
      },
      body: JSON.stringify({ ...androidForm, source: "marketing_android_badge" }),
    })
      .then((res) => setAndroidStatus(res.ok ? "sent" : "error"))
      .catch(() => setAndroidStatus("error"));
  }

  return (
    <>
      <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
        <a
          href="https://apps.apple.com/mx/app/tippy-agenda-ai/id6762086950?l=en-GB"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Descargar Tippy en App Store"
          className="inline-flex transition-opacity hover:opacity-85"
        >
          <img
            src="/marketing/ios.svg"
            alt="Download on the App Store"
            className="h-9 w-auto"
          />
        </a>
        <button
          type="button"
          onClick={() => setAndroidModalOpen(true)}
          aria-label="Solicitar acceso a Tippy en Google Play"
          className="inline-flex transition-opacity hover:opacity-85"
        >
          <img
            src="/marketing/android.png"
            alt="Get it on Google Play"
            className="h-9 w-auto"
          />
        </button>
      </div>

      {androidModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-2xl bg-background p-6 shadow-xl border border-border">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-xl font-semibold text-foreground">Android en prueba cerrada</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Déjanos el correo de Google que usas en Play Store. Te agregaremos al programa de pruebas y te enviaremos el link de instalación.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAndroidModalOpen(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            {androidStatus === "sent" ? (
              <div className="rounded-xl bg-primary/10 p-4 text-sm text-foreground">
                Gracias. Te agregaremos al testing cerrado y te enviaremos el link de Google Play.
              </div>
            ) : (
              <form onSubmit={handleAndroidSubmit} className="space-y-3">
                <input
                  type="text"
                  required
                  value={androidForm.name}
                  onChange={(e) => setAndroidForm({ ...androidForm, name: e.target.value })}
                  placeholder="Nombre"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                />
                <input
                  type="email"
                  required
                  value={androidForm.google_email}
                  onChange={(e) => setAndroidForm({ ...androidForm, google_email: e.target.value })}
                  placeholder="Correo asociado a Play Store"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                />
                <input
                  type="tel"
                  value={androidForm.whatsapp}
                  onChange={(e) => setAndroidForm({ ...androidForm, whatsapp: e.target.value })}
                  placeholder="WhatsApp (opcional)"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                />
                {androidStatus === "error" && (
                  <p className="text-sm text-destructive">No pudimos enviar la solicitud. Intenta de nuevo.</p>
                )}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={androidStatus === "sending"}
                    className="inline-flex flex-1 items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background hover:bg-foreground/90 disabled:opacity-60"
                  >
                    {androidStatus === "sending" ? "Enviando..." : "Solicitar acceso Android"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
